import React, { useEffect, useState } from 'react';
import { rememberLanguage } from './voice.js';
import { call, getToken, clearToken, replaceToken } from './api.js';
import { withReturn, hereNow } from './gate.jsx';
import { upload, uploadsEnabled } from './upload.js';
import { TabBar } from './ui.jsx';
import { useSaved } from './saved.js';
import { go } from './nav.js';
import { Eye } from './Eye.jsx';
/* ⚠️ BOTH LISTS ARE GENERATED, AND NEITHER IS TYPED HERE. `countries.js` comes
 * from tools/generators/countries.py (181 countries — before it existed every
 * screen typed its own 23 and a Nepali cook in a Hong Kong kitchen could not
 * sign in) and `locales.js` from locales.py, out of the migrations themselves.
 * Both generators write the locked screens' copies in the same breath, so a
 * React list and a locked-screen list cannot disagree. */
import { splitPhone, joinPhone, PhoneRow } from './phone.jsx';
import { LAUNCH, MAJOR } from './locales.js';
import { needs } from './password.js';
import { PwChecklist } from './pwcheck.jsx';

/* ══════════════════════════════════════════════════════════════════════════
 * PROFILE · SETTINGS · NOTIFICATIONS
 *
 * Three locked screens — 08_Profile, 08e_Settings, 08g_Notifications — and
 * one file, because they are the same account seen three ways and every one
 * of them reads GET /settings/me. Three files would be three places for the
 * Eleater ID, the shown name and the sign-out to drift.
 *
 * ── THE NAME OTHER PEOPLE SEE IS THE SERVER'S ANSWER ────────────────────────
 *
 * ⚠️ `shown_name` comes back already assembled, by `eleat_shown_name()` —
 * the same function the other eight queries ask. settings.service.js says it
 * in its own comment: *"A screen that assembles 'first name + last initial'
 * itself is the ninth copy of the rule."* So this screen prints what it is
 * given and never builds a name.
 *
 * ── AND THE ELEATER ID IS NOT A COUNTER ─────────────────────────────────────
 *
 * ⚠️ It arrives formatted — `EL-3333-7021` — from `formatEleaterId()`.
 * Migration 058 shuffles the counter before storing it, so two Eleaters
 * cannot subtract their IDs and read how fast Eleat is growing. Nothing here
 * reformats it, pads it or counts with it.
 * ══════════════════════════════════════════════════════════════════════════ */

export default function Profile({ view }) {
  const [me, setMe] = useState(null);
  const [err, setErr] = useState(null);
  const signedIn = !!getToken();

  useEffect(() => {
    if (!signedIn) return;
    let live = true;
    call('/settings/me', { auth: true })
      .then((d) => live && setMe(d))
      .catch((e) => live && setErr(e.message));
    return () => { live = false; };
  }, [signedIn]);

  /* ══════════════════════════════════════════════════════════════════════
     ⚠️⚠️ THE COUNTS — `08_Profile.html` PUTS A NUMBER ON SAVED, RESERVATIONS
     AND REVIEWS, AND REACT HAD NONE.

     Its own script fills `.cnt` from `Eleat.saved().length`,
     `Eleat.reservations().length` and `Eleat.reviews().length`, and the comment
     beside it records the fault it fixed: the counts *"were OFF BY ONE, AND ONE
     READ A PHASE 2 TABLE"* — Orders had been removed from the list but the
     indexes still assumed four rows, so `cnts[2]` was fed the orders count
     (a table no surface may read) and the Reviews count went to `cnts[3]`,
     which does not exist. **Nothing looked broken; it was just wrong.**

     ⚠️ SO THIS IS KEYED BY NAME, NOT BY POSITION. `counts.reviews` cannot end
     up on the Reservations row however the list is re-ordered, which is the
     entire class of fault that comment describes.

     ⚠️ AND ALL THREE NUMBERS COME FROM ROUTES THAT ALREADY EXIST — nothing new
     was added to the server. `useSaved()` has the saved set in memory already,
     so Saved costs no request at all.

     ⚠️ A FAILED COUNT SHOWS NOTHING, NEVER A ZERO. A blank slot says "we do not
     know"; a `0` says "you have none", and those are different sentences. */
  const savedIds = useSaved();
  const [reservationN, setReservationN] = useState(null);
  const [reviewN, setReviewN] = useState(null);

  useEffect(() => {
    if (!signedIn) return;
    let live = true;
    /* the answers are {reservations:[…]} and {reviews:[…]} — read those too
       (audit, 7 Oct 2026: both counts were always blank) */
    const len = (d) => (Array.isArray(d) ? d.length
      : d && Array.isArray(d.results) ? d.results.length
      : d && Array.isArray(d.reservations) ? d.reservations.length
      : d && Array.isArray(d.reviews) ? d.reviews.length : null);
    call('/reservations/mine', { auth: true })
      .then((d) => live && setReservationN(len(d))).catch(() => {});
    call('/reviews/mine', { auth: true })
      .then((d) => live && setReviewN(len(d))).catch(() => {});
    return () => { live = false; };
  }, [signedIn]);

  const counts = {
    saved: savedIds.size,
    reservations: reservationN,
    reviews: reviewN,
  };

  /* ══════════════════════════════════════════════════════════════════════
     ⚠️ THE NUDGE'S THREE-WAY RULE, QUOTED FROM THE LOCKED SCRIPT'S OWN
     COMMENT: *"IT READS THE REAL ANSWER, and if it cannot find one it says
     nothing rather than guessing. A row that claims a profile is finished when
     nothing was ever recorded is the same lie as a prompt that never clears."*

       · they have chosen cuisines  → the ordinary subtitle, no prompt
       · the answer has not arrived → the ordinary subtitle, no prompt
       · they have chosen NONE      → the prompt

     `/settings/me` returns `cuisine_prefs`, so `me === null` is the middle
     case and is deliberately not a nudge — prompting on a screen that is still
     loading is a prompt that appears and vanishes. */
  const prefsNudge = !!me && Array.isArray(me.cuisine_prefs)
    && me.cuisine_prefs.length === 0;

  if (!signedIn) {
    /* ⚠️ THE SCREEN SAYS WHICH SCREEN IT IS — walked live, 12 Oct 2026: the
       bell, signed out, opened a page titled "Profile" with Profile lit. */
    const title = view === 'notifications' ? 'Notifications' : view === 'settings' ? 'Settings' : 'Profile';
    return (
      <div className="home">
        <div className="top"><h1 className="heroline">{title}</h1></div>
        <main>
          {/* `08_Profile.html #pfGuest` — one button, the locked words */}
          <div className="guest">
            <a className="gcta" href={withReturn('?welcome=1', hereNow())}>Sign in or join Eleat</a>
          </div>
        </main>
        <TabBar active={view === 'notifications' ? null : 'profile'} />
      </div>
    );
  }

  if (view === 'notifications') return <Notifications me={me} />;
  if (view === 'settings') return <Settings me={me} setMe={setMe} err={err} />;

  return (
    <div className="home">
      <div className="top">
        <a className="back" href="?" aria-label="Home">‹</a>
        <h1 className="heroline">Profile</h1>
      </div>

      <main>
        {err && <p className="err">{err}</p>}
        {!me && !err && <p className="mut">Loading…</p>}

        {me && (
          <>
            {/* ══════════════════════════════════════════════════════════════
                ⚠️ THE AVATAR IS BESIDE THE WORDS, NOT ABOVE THEM.
                Al, 23 September 2026: *"in profile its all off aligned and even
                the eleater number is not in the correct position."*
                `08_Profile.html` puts the 66px avatar on the LEFT and the block
                beside it — "Your Eleat profile" over "Eleater ID · …" over
                "Date of birth · …". React centred the avatar and stacked a bare
                name and a bare number underneath, so the ID looked like a
                caption on a photograph instead of a labelled field.
                ⚠️ AND THE LABELS ARE THE POINT. A bare `EL-5834-4487` under a
                name tells nobody what it is. The locked screen says what every
                line is, in words. */}
            <div className="pident">
              <Avatar me={me} setMe={setMe} />
              <div>
                {/* ⚠️ THEIR NAME, NOT A LABEL — Al-Noor, 21 Oct 2026: "should
                    it not say my name". The locked heading is the fallback
                    when Eleat has no name yet. */}
                <div className="hi">{String(me.shown_name || me.display_name || me.first_name || '').trim() || 'Your Eleat profile'}</div>
                {/* ⚠️ THE ID IS SHOWN EXACTLY AS IT ARRIVES. No padding, no
                    re-formatting, and never used as a count. */}
                <div className="cid">Eleater ID · {me.eleater_id || '—'}</div>
                {/* ⚠️ NOTHING ABOUT THE BIRTH DATE UNDER THE NAME — Al-Noor,
                    21 Oct 2026. Sign-up already saves it to the profile
                    (auth.service.js → eleater_profiles.date_of_birth); it
                    lives in Settings and is never shown here. */}
              </div>
            </div>

            {/* ⚠️ THE ELEATER'S OWN RATING — `08_Profile.html .erate`, and it
                was missing entirely. `users.eleater_rating` and
                `eleater_rating_count` are migrations 022/023, built so Eleat
                can tell somebody how restaurants rate them. Below three
                ratings there is no number, exactly as a venue below five
                reviews has none — so the em-dash and the NEW ELEATER pill are
                the real state, not a placeholder. */}
            <div className="erate">
              <div className="erhead">
                <div className="ertitle">Your Eleat rating</div>
                {!(me.eleater_rating_count >= 3) && (
                  <span className="erpill">NEW ELEATER</span>
                )}
              </div>
              <div className="erscore">
                {me.eleater_rating_count >= 3 && me.eleater_rating != null
                  ? <span className="erval">{Number(me.eleater_rating).toFixed(1)}</span>
                  : <span className="erdash">—</span>}
              </div>
              <div className="ernote">
                Restaurants rate you after a visit, just as you rate them.
                Ratings are anonymous. Yours is the average of your most recent
                visits, and appears once three restaurants have rated you.
              </div>
            </div>

            {/* ⚠️ EVERY ROW HAS A SUBTITLE AND A CHEVRON — `08_Profile.html
                .item`: an icon block, a title, a line saying what is behind it,
                and the arrow. React had bare text links, which is why the
                screen read as a list of words rather than a menu. The subtitles
                are the locked screen's own. */}
            {/* ══════════════════════════════════════════════════════════
                ⚠️⚠️ SIX ROWS, IN TWO GROUPS, IN THIS ORDER — `08_Profile.html`
                Al, 24 September 2026: *"if you go into profile, the whole list
                is fucked. You've got it in different orders and everything
                else."*  He is right. React had NINE rows in ONE flat list:
                Saved · Reservations · Your reviews · Location · Notifications ·
                Settings · Preferences · Notification settings · Help.
                The locked screen has:

                  group 1   Saved · Reservations · Reviews
                  group 2   Preferences · Settings · Help
                  then      Log out

                ⚠️ THE TWO GROUPS ARE THE MEANING, NOT DECORATION. The first is
                what the Eleater has DONE — their places, their tables, their
                reviews. The second is how Eleat BEHAVES for them. One flat
                list of nine says those are the same kind of thing.

                ⚠️ AND THREE OF MY NINE DO NOT BELONG HERE AT ALL. Location,
                Notifications and Notification settings are reached from
                SETTINGS on the locked screen, not from Profile — so they move
                there rather than disappearing, because a screen nothing links
                to is a screen that does not exist.

                ⚠️ IT IS "Reviews", NOT "Your reviews". RULE 4 — the locked
                screen's own word. */}
            <nav className="plist">
              <Row href="?saved=1" t="Saved" s="Hearted restaurants"
                icon="saved" n={counts.saved} />
              {/* ⚠️ HELP PROMISED THIS ROW TWICE — "its status shows in
                  Reservations on your profile" — and the row did not exist. */}
              <Row href="?reservations=1" t="Reservations" s="Upcoming & past"
                icon="reservations" n={counts.reservations} />
              <Row href="?reviews=1" t="Reviews" s="Your verified reviews"
                icon="reviews" n={counts.reviews} />
            </nav>

            <nav className="plist">
              {/* ⚠️⚠️ THE SUBTITLE WAS HERE ALL ALONG AND I WROTE THAT IT WAS NOT.
                  This comment used to read *"NO SUBTITLE ON PREFERENCES. The
                  locked row carries none."*  `08_Profile.html` line 273:
                  `<div class="s" id="prefSub">Cuisine, dietary, vibe, budget</div>`.
                  **I invented an absence and then justified it** — the same
                  shape as every other sentence of mine that got mistaken for a
                  decision. It has an id BECAUSE the nudge swaps it, which is
                  the thing I half-remembered and drew the wrong conclusion from.

                  ⚠️ AND THE NUDGE IS A STATE OF THIS ROW, NEVER A SECOND CARD.
                  Al, 16 Sep 2026: *"finish your profile and preferences are the
                  same fucking thing. why the redundancy."*  Both cards went to
                  the same screen, three inches apart, one dressed as a task —
                  so the prompt moved onto this row. One control, two states. */}
              <Row href="?preferences=1" t="Preferences"
                icon="preferences"
                s={prefsNudge
                  ? 'Add your cuisines and tastes so Zaryna finds you better spots'
                  : 'Cuisine, dietary, vibe, budget'}
                nudge={prefsNudge} />
              <Row href="?settings=1" t="Settings" icon="settings"
                s="Account, notifications, privacy, language" />
              <Row href="?help=1" t="Help" icon="help"
                s="FAQs, your account, reservations, reviews" />
            </nav>

            {/* ⚠️ SIGNING OUT CLEARS THE TOKEN AND RELOADS. Leaving a stale
                token behind means the next screen asks with a key that no
                longer belongs to anybody. */}
            <SignOutButton className="pbtn signout">Sign out</SignOutButton>
          </>
        )}
      </main>

      <TabBar active="profile" />
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════════
 * THE AVATAR — INITIALS UNTIL THEY CHOOSE OTHERWISE
 *
 * ⚠️ AL, 22 SEPTEMBER 2026: *"Profile goes in the profile. And if the Eleater
 * wants to put it in, they'll put it in… Until then, they get their initials
 * in their profile. So, or they'll pick an avatar."*
 *
 * ⚠️ AND IT IS NOT PART OF JOINING. I had it listed as a sign-up step, which
 * it never was — nothing should stand between somebody and the app on the way
 * in. A photo is something you add later, if you feel like it.
 *
 * ── ⚠️ A REAL PHOTO CANNOT BE UPLOADED YET, AND IT IS NOT A SCREEN ──────────
 *
 * `eleater_profiles.avatar_url` exists — and there is NO upload anywhere in
 * the backend. No storage, no multipart handler, nothing that can receive a
 * file, and `avatar_url` is not in `mineSchema` so it cannot even be set by
 * URL. The schema itself already flags it: a column *"that stays NULL for
 * ever"*.
 *
 * That is file storage — where the file lives, who pays for it, what happens
 * when somebody uploads something they should not. A decision, not a screen,
 * and it is Al's. Until then the initials ARE the avatar, and they are a
 * complete answer rather than a placeholder.
 *
 * ── THE INITIALS COME FROM THE NAME THE SERVER ALREADY BUILT ────────────────
 *
 * ⚠️ `shown_name` arrives assembled by `eleat_shown_name()`. Taking the
 * initials from first_name + last_name here would be a SECOND rule about what
 * an Eleater is called, and it would disagree with the name printed directly
 * underneath it the moment somebody sets a preferred name.
 */
/* ══════════════════════════════════════════════════════════════════════════
 * ONE PROFILE ROW — `08_Profile.html .item`
 *
 * An icon block, the label, the line under it saying what is behind the row,
 * and the chevron. React had a bare `<a>` with a word in it, which is why Al
 * said the screen looked *"all off aligned"* — there was nothing to align.
 * ══════════════════════════════════════════════════════════════════════════ */
/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️ THE SIX ICONS — `08_Profile.html` DRAWS ONE IN EVERY ROW AND REACT
 * DREW SIX EMPTY SQUARES.
 *
 * `<span className="gi" aria-hidden="true" />` — a styled 20px box with
 * nothing in it, on all six rows. Nothing threw, nothing logged, and the list
 * read as a column of blank tiles beside the words. **That is part of what
 * "the whole list is fucked" looks like**, and re-ordering rows that were
 * already in the right order would never have found it.
 *
 * ⚠️ EVERY PATH IS COPIED OUT OF THE LOCKED FILE, not drawn again (RULE 4) —
 * a second hand-drawn heart is a heart that drifts, which is the exact fault
 * `saved.js` records against the card.
 *
 * ⚠️ AND TWO OF THEM CARRY A COLOUR, WHICH IS THE LOCKED SCREEN'S OWN CHOICE:
 * Saved is `#AD1A1A` — the palette's Heart, written as the token `--heart`
 * (RULE ZERO-A: the token, never the hex) — and Reviews is `var(--action)`.
 * The other four inherit, exactly as they do there.
 * ══════════════════════════════════════════════════════════════════════════ */
const ICONS = {
  /* a filled heart — `08_Profile.html` Saved row, `style="color:#AD1A1A"` */
  saved: {
    tint: 'var(--heart)',
    svg: <path d="M12 21s-7-4.6-9.3-9C1 8.5 2.7 5 6 5c2 0 3.2 1.1 4 2.3C10.8 6.1 12 5 14 5c3.3 0 5 3.5 3.3 7-2.3 4.4-9.3 9-9.3 9z" fill="currentColor" />,
  },
  /* a calendar — Reservations */
  reservations: {
    svg: (
      <g fill="none" stroke="currentColor" strokeWidth="1.7"
        strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4.5" width="18" height="16.5" rx="2.5" />
        <path d="M3 9.5h18M8 2.5v4M16 2.5v4" />
      </g>
    ),
  },
  /* a filled star — Reviews, `style="color:var(--action)"` */
  reviews: {
    tint: 'var(--action)',
    svg: <path d="M12 3.2l2.6 5.3 5.8.8-4.2 4.1 1 5.8L12 16.6l-5.2 2.7 1-5.8L3.6 9.3l5.8-.8z" fill="currentColor" />,
  },
  /* a fork and knife — Preferences */
  preferences: {
    svg: (
      <path d="M7 3v8M5 3v4a2 2 0 002 2M17 3c-1.5 0-2.5 2-2.5 4.5S15.5 12 17 12v9M7 11v10"
        fill="none" stroke="currentColor" strokeWidth="1.7"
        strokeLinecap="round" strokeLinejoin="round" />
    ),
  },
  /* a gear — Settings */
  settings: {
    svg: (
      <g fill="none" stroke="currentColor" strokeWidth="1.7"
        strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1" />
      </g>
    ),
  },
  /* a question mark in a circle — Help */
  help: {
    svg: (
      <>
        <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.7" />
        <path d="M9.6 9.3a2.5 2.5 0 114.3 1.8c-.9.8-1.9 1.3-1.9 2.6"
          fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        <circle cx="12" cy="17" r="1.05" fill="currentColor" />
      </>
    ),
  },
};

/**
 * One Profile row.
 *
 * @param icon  a key of ICONS — the locked screen has one on every row.
 * @param n     the count slot. `08_Profile.html` puts `<div class="cnt">` on
 *              Saved, Reservations and Reviews and fills it from the real
 *              numbers — **blank when zero**, which is its own rule: a grey
 *              `0` beside Reviews tells somebody they have failed at something.
 * @param nudge the Preferences prompt state (`.pnudge-on`).
 */
function Row({ href, t, s, icon = null, n = null, nudge = false, onClick }) {
  const ic = icon ? ICONS[icon] : null;
  return (
    <a className={nudge ? 'item pnudge-on' : 'item'} href={href} onClick={onClick}>
      {/* the icon tile only where the row HAS an icon — Settings' rows
          (`08e_Settings.html` 527–538) have none, and drew five empty
          squares (look walk, 9 Oct 2026) */}
      {ic && (
        <span className="gi" aria-hidden="true"
          style={ic.tint ? { color: ic.tint } : undefined}>
          <svg width="20" height="20" viewBox="0 0 24 24">{ic.svg}</svg>
        </span>
      )}
      <span className="lab">
        <span className="t">{t}</span>
        {s && <span className="s">{s}</span>}
      </span>
      {/* ⚠️ `n > 0`, NOT `n != null` — see above. And the slot is drawn empty
          rather than omitted, so the chevrons stay in one column. */}
      {n !== null && <span className="cnt">{n > 0 ? n : ''}</span>}
      <span className="chev" aria-hidden="true">›</span>
    </a>
  );
}

function Avatar({ me, setMe }) {
  const fileRef = React.useRef(null);
  const [busy, setBusy] = useState(false);
  const [can, setCan] = useState(false);
  const [oops, setOops] = useState(null);

  /* ⚠️ ASKED, NEVER ASSUMED. Offering a camera badge when there is nowhere to
     put the photo produces the worst outcome in this project: a control that
     appears to work and stores nothing. If storage is not configured the badge
     is simply not drawn — and the initials are a complete resting state, not a
     half-finished profile. The locked screen says so itself: "the initials are
     not a loading state." */
  useEffect(() => {
    let live = true;
    uploadsEnabled().then((v) => live && setCan(v));
    return () => { live = false; };
  }, []);

  if (!me) return null;

  async function picked(e) {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';              /* so the same file can be picked twice */
    if (!f || busy) return;
    setBusy(true); setOops(null);
    try {
      const { key } = await upload(f);
      /* ⚠️ THE KEY IS STORED, NEVER THE ADDRESS. settings.service.js turns it
         back into a URL on the way out, through the one function that knows the
         public host. A screen that assembles the address itself is the second
         thing that knows the rule, and the day the host changes one of them is
         wrong. */
      setMe(await call('/settings/me', {
        /* ⚠️ PUT, NOT PATCH — there is no PATCH on /settings/me and there never
             was. Live it answered 404, so add-photo and remove-photo were both
             dead the moment storage was switched on. `avatar_url` is already in
             the route's schema; only the verb was wrong. */
          method: 'PUT', auth: true, body: { avatar_url: key },
      }));
    } catch (e2) {
      setOops(e2.message);
    } finally { setBusy(false); }
  }

  async function badge() {
    /* ⚠️ IF THERE IS ALREADY A PICTURE, OFFER TO REMOVE IT — and the sentence
       is the locked screen's own, not a new one. `08_Profile.html`: "Remove
       your picture and go back to your initials?" Its reasoning too: "an avatar
       you cannot remove is a decision you cannot undo." */
    if (me.avatar_url) {
      if (!window.confirm('Remove your picture and go back to your initials?')) return;
      setBusy(true); setOops(null);
      try {
        setMe(await call('/settings/me', {
          method: 'PUT', auth: true, body: { avatar_url: null },
        }));
      } catch (e2) { setOops(e2.message); } finally { setBusy(false); }
      return;
    }
    fileRef.current?.click();
  }

  /* ⚠️ THE BADGE SITS ON THE AVATAR, not in a menu three taps away — the locked
     screen's own reasoning: "The photo is the thing being changed, so the
     control belongs on the thing." The label and the camera glyph below are
     `08_Profile.html`'s, character for character.

     ⚠️ AND IT IS ON PROFILE, NOT JOINING. Al, 22 September 2026: *"Why at sign
     up? We don't need the profile photo at the sign up… Profile goes in the
     profile. And if the Eleater wants to put it in, they'll put it in."* */
  const Badge = () => (can ? (
    <>
      <button type="button" className="avedit" onClick={badge} disabled={busy}
        aria-label="Add or change your picture">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
          <circle cx="12" cy="13" r="4" />
        </svg>
      </button>
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={picked} />
    </>
  ) : null);

  /* ⚠️ THE MESSAGES SIT OUTSIDE THE 72px WRAPPER. Inside it they would wrap to
     one word a line, which is how a working control reads as a broken one. */
  const Say = () => (
    <>
      {busy && <p className="mut">Sending…</p>}
      {oops && <p className="err">{oops}</p>}
    </>
  );

  if (me.avatar_url) {
    return (
      <>
        <div className="pavwrap">
          <img className="pavatar" src={me.avatar_url} alt="" />
          <Badge />
        </div>
        <Say />
      </>
    );
  }
  /* ⚠️ ONE OR TWO LETTERS, FROM THE SHOWN NAME. "Al Pirani" → AP.
     "Al P." → AP. A single name → one letter, not a padded blank. */
  const name = String(me.shown_name || me.display_name || me.first_name || '').trim();
  const initials = name
    .split(/\s+/).filter(Boolean).slice(0, 2)
    .map((w) => w[0]).join('').toUpperCase();
  return (
    <>
      <div className="pavwrap">
        <div className="pavatar initials" aria-hidden="true">
          {initials || '\u00b7'}
        </div>
        <Badge />
      </div>
      <Say />
    </>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * SETTINGS — `08e_Settings.html`, and it was the largest gap in the app
 *
 * Al, 24 September 2026: *"if you go into profile, the whole list is fucked.
 * You've got it in different orders and everything else. And there's just
 * something missing… go through each and every single HTML that we've locked
 * in."*
 *
 * The locked screen has THREE sections and React had one flat run of five
 * boxes. In order, it is: ACCOUNT behind an Edit lock — First name · Last
 * name · Preferred name · Mobile (dial code + number) · Email (+ Verify) ·
 * Password · Eleater ID — then ABOUT YOU — Date of birth · Gender · Preferred
 * language · Other languages you speak — then PREFERENCES, five rows with no
 * subtitles, then Save changes, then Delete account.
 *
 * ⚠️ THE SUBTITLES ON THE PREFERENCES ROWS WERE MINE AND THEY ARE GONE.
 * "What has happened", "How we reach you", "Where you are browsing" are not
 * in the locked file. RULE 4 — do not invent Eleat, not even a helpful line.
 *
 * ── NOTHING CAN BE CHANGED UNTIL EDIT IS PRESSED ────────────────────────────
 *
 * Al, audit item 10.3: *"settings needs an edit button before anything can be
 * changed."*  ⚠️ AND A `<select>` AND A `<button>` HAVE NO `readonly` — only
 * `disabled`. The locked file's own comment records both falling through that
 * gap: the dial code and the gender dropdown sat live on a locked card.
 *
 * ⚠️ ONLY THE FIELDS `PUT /settings/me` ACCEPTS. Its schema is `.strict()`, so
 * a box for anything else is a box that 400s on Save — which is exactly how
 * gender and languages were unreachable for weeks.
 * ══════════════════════════════════════════════════════════════════════════ */

/* ⚠️ THE FOUR VALUES ARE THE SCHEMA'S OWN — constraint
 * `eleater_gender_is_one_of_four`, migration 084. The ORDER and the LABELS are
 * the locked screen's: "Prefer not to say" is the empty option and comes
 * first, then Female, Male, Other. */
/* ⚠️ "Prefer not to say" CHOSEN IS SAVED AS SUCH — Al, 1 Oct 2026: "prefer
   not to say" is data too (restaurant Analytics counts it); it was saved as no
   answer at all (audit, 7 Oct 2026). Never chosen still shows this option. */
const GENDERS = [
  { v: 'prefer_not_to_say', label: 'Prefer not to say' },
  { v: 'female', label: 'Female' },
  { v: 'male', label: 'Male' },
  { v: 'other', label: 'Other' },
];

const SETTINGS_PLACE = 'eleat.settingsScroll';
function rememberSettingsPlace() {
  try { sessionStorage.setItem(SETTINGS_PLACE, String(window.scrollY || 0)); } catch { /* refused */ }
}

function Settings({ me, setMe, err }) {
  /* coming back from a policy: put them where they were */
  useEffect(() => {
    let y = null;
    try { y = sessionStorage.getItem(SETTINGS_PLACE); sessionStorage.removeItem(SETTINGS_PLACE); } catch { /* refused */ }
    if (y != null) setTimeout(() => window.scrollTo(0, Number(y) || 0), 30);
  }, []);

  const [draft, setDraft] = useState(null);
  const [open, setOpen] = useState(false);       // the Edit lock
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveErr, setSaveErr] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (me && !draft) {
      setDraft({
        first_name: me.first_name || '',
        last_name: me.last_name || '',
        display_name: me.display_name || '',
        email: me.email || '',
        /* ⚠️ THE CODE AND THE NUMBER ARE TWO FIELDS. Al, 16 September 2026:
           *"other boxes for cell humber with the country code drop box of all
           countries then the box for the phone number."*  It was one box with
           the whole string in it, so the dial code could only be changed by
           editing text. They are joined back together on the way out. */
        dial: splitPhone(me.phone).dial,
        number: splitPhone(me.phone).number,
        preferred_language: me.preferred_language || 'en',
        gender: me.gender || '',
        languages_spoken: me.languages_spoken || [],
        languages_spoken_other: me.languages_spoken_other || '',
      });
    }
  }, [me, draft]);

  const set = (k, v) => { setDraft((d) => ({ ...d, [k]: v })); setSaved(false); };

  async function save() {
    setSaving(true); setSaveErr(null);
    try {
      /* ⚠️ ONLY WHAT CHANGED, and never an empty string where the Eleater
         simply has not filled a box in — sending '' would overwrite a name
         with nothing, which is a deletion nobody asked for.
         ⚠️ GENDER IS THE EXCEPTION AND IT HAS TO BE. Empty means "Prefer not
         to say", which is a real answer and the way somebody takes back one
         they gave by accident; the route takes null for exactly that. */
      const body = {};
      const text = ['first_name', 'last_name', 'display_name', 'email'];
      for (const k of text) {
        const v = (draft[k] || '').trim();
        if (v && v !== (me[k] || '')) body[k] = v;
        /* the two optional names CAN be emptied — a box the Eleater cleared
           on purpose (it held something before) is sent as '', which the
           server reads as "remove it" (audit, 7 Oct 2026: it could never be
           cleared). The required name and the email are never sent empty. */
        else if (!v && (me[k] || '') && (k === 'last_name' || k === 'display_name')) body[k] = '';
      }
      const phone = joinPhone(draft.dial, draft.number);
      if (phone && phone !== (me.phone || '')) body.phone = phone;
      if (draft.preferred_language !== (me.preferred_language || 'en')) {
        body.preferred_language = draft.preferred_language;
      }
      if ((draft.gender || '') !== (me.gender || '')) body.gender = draft.gender || null;
      if (!sameList(draft.languages_spoken, me.languages_spoken)) {
        body.languages_spoken = draft.languages_spoken;
      }
      if ((draft.languages_spoken_other || '').trim() !== (me.languages_spoken_other || '')) {
        body.languages_spoken_other = draft.languages_spoken_other.trim();
      }

      if (Object.keys(body).length === 0) { setSaving(false); setSaved(true); setOpen(false); return; }
      const d = await call('/settings/me', { method: 'PUT', auth: true, body });
      setMe((prev) => ({ ...prev, ...d }));
      setSaved(true);
      setOpen(false);
      /* ⚠️ A NEW LANGUAGE CHANGES THE WHOLE APP (i18n.js) — the same choice
         as Home's language menu, so it is remembered the same way. */
      if (body.preferred_language) {
        rememberLanguage(body.preferred_language);
        window.location.reload();
      }
    } catch (e) {
      setSaveErr(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    /* ⚠️ `settings` IS A SCOPE, NOT DECORATION. The locked screen's class names
       — `.in`, `.save`, `.lab`, `.fld` — are kept verbatim so the correspondence
       between this file and `08e_Settings.html` can be read at a glance. But
       `.in` and `.save` ALREADY MEAN SOMETHING ELSE in eleat.css: `.in` is an
       information panel and `.save` is the heart control. Using them unscoped
       would have re-skinned two unrelated things across the whole app, which is
       the fault the heart and the rails both cost a day to. Every rule added for
       this screen is written `.settings .x`. */
    <div className="home settings">
      <div className="top">
        {/* ⚠️ BACK GOES TO PROFILE — `08e_Settings.html` line 246:
            `<a class="back" href="08_Profile.html">`. Settings is reached from
            Profile, so that is where back goes. */}
        <a className="back" href="?profile=1" aria-label="Profile">‹</a>
        <h1 className="heroline">Settings</h1>
      </div>

      {/* ⚠️ A LOCKED FIELD OPENS THE LOCK — look walk, 9 Oct 2026. After a
          save the screen locks again, and tapping the language box or a chip
          did nothing at all: a tester filled her name, saved, and could not
          change her language. The Edit lock stays (08e_Settings); a tap on
          any field in it now opens it, the same as pressing Edit. */}
      <main onClick={(e) => { if (!open && e.target.closest && e.target.closest('.group')) setOpen(true); }}>
        {err && <p className="err">{err}</p>}
        {!draft && !err && <p className="mut">Loading…</p>}

        {draft && (
          <>
            {/* ── ACCOUNT ─────────────────────────────────────────────── */}
            <div className="sechead">
              <div className="sec">ACCOUNT</div>
              {/* ⚠️ ONE BUTTON, TWO LABELS — `stEdit` / `stEditLabel`. Pressing
                  Done does not save; Save changes at the bottom does. The
                  locked screen keeps them separate and so does this. */}
              <button type="button" className="editbtn" aria-pressed={open}
                onClick={() => setOpen((o) => !o)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                  strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 20h9" />
                  <path d="M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4z" />
                </svg>
                <span>{open ? 'Done' : 'Edit'}</span>
              </button>
            </div>

            <div className="group">
              <Fld label="First name">
                <input className="in" type="text" autoComplete="given-name"
                  placeholder="First name" readOnly={!open} value={draft.first_name}
                  onChange={(e) => set('first_name', e.target.value)} />
              </Fld>
              <Fld label="Last name">
                <input className="in" type="text" autoComplete="family-name"
                  placeholder="Last name" readOnly={!open} value={draft.last_name}
                  onChange={(e) => set('last_name', e.target.value)} />
              </Fld>
              {/* ⚠️ "Preferred name", NOT "Display name". React called it
                  Display name and invented the hint under it. The locked label
                  carries its own question in muted type. Migration 088 settles
                  what the COLUMN means; the screen's word for it is Al's. */}
              <Fld label="Preferred name" aside="— what should we call you?">
                <input className="in" type="text" autoComplete="nickname"
                  placeholder="Your preferred name" readOnly={!open}
                  value={draft.display_name}
                  onChange={(e) => set('display_name', e.target.value)} />
              </Fld>

              {/* ⚠️ MOBILE COMES BEFORE EMAIL on the locked screen. React had
                  them the other way round. */}
              <Fld label="Mobile number"
                badge={me.phone_verified ? '✓ verified' : null}>
                {/* the same country picker as joining — flag, +code, search
                    (Al-Noor, 30 Oct 2026: "it has to match"); locked until Change */}
                <PhoneRow dial={draft.dial} number={draft.number} disabled={!open}
                  onDial={(d) => set('dial', d)} onNumber={(n) => set('number', n.replace(/\D/g, ''))} />
              </Fld>

              <Fld label="Email" required
                badge={me.email_verified ? '✓ verified' : null}>
                <div className="two">
                  <input className="in" type="email" inputMode="email"
                    autoComplete="email" autoCapitalize="off" spellCheck={false}
                    placeholder="you@example.com" style={{ flex: 2 }}
                    readOnly={!open} value={draft.email}
                    onChange={(e) => set('email', e.target.value)} />
                  {!me.email_verified && <VerifyEmail me={me} setMe={setMe} />}
                </div>
              </Fld>

              {/* joined with Google or Apple → no password row: they sign in
                  with it (Al-Noor, 30 Oct 2026). Joined by email → shown. */}
              {!['google', 'apple'].includes(me.signup_method) && (
                <SetPassword me={me} setMe={setMe} open={open} />
              )}

              <Fld label="Eleater ID">
                <div className="static">
                  <span>{me.eleater_id || '—'}</span>{' '}
                  <span className="lock">Can&rsquo;t be changed</span>
                </div>
              </Fld>
            </div>

            {/* ── ABOUT YOU ───────────────────────────────────────────── */}
            <div className="sec">ABOUT YOU</div>
            <div className="group">
              {/* ⚠️ SHOWN, NEVER ASKED. Al, 7 Sep 2026: *"they cant keep
                  changing their dob. once its in at sign up they cant change
                  it."*  It is returned by the route and is not in its schema,
                  so it cannot be written back even by a crafted request. */}
              <Fld label="Date of birth">
                <div className="in locked" aria-readonly="true">
                  {me.date_of_birth ? String(me.date_of_birth).slice(0, 10) : '—'}
                </div>
              </Fld>

              <Fld label="Gender" optional>
                <select className="in" disabled={!open} value={draft.gender}
                  onChange={(e) => set('gender', e.target.value)}>
                  {GENDERS.map((g) => (
                    <option key={g.v} value={g.v}>{g.label}</option>
                  ))}
                </select>
                <div className="help">
                  Only ever reported as a group, never against your name. It
                  helps Eleat show restaurants who they are actually reaching.
                </div>
              </Fld>

              {/* ⚠️ THREE, AND THEY COME FROM THE MIGRATIONS. `locales.js` is
                  written by locales.py out of `4. Backend/migrations/` — the
                  same generator that writes the locked screen's list, so the
                  two cannot drift. Al, 9 Sep 2026: "we keep the 3 in the
                  dropdown." ⚠️ AND THE WORD IS DROPDOWN, never "picker". */}
              <Fld label="Preferred language">
                <select className="in" disabled={!open}
                  value={draft.preferred_language}
                  onChange={(e) => set('preferred_language', e.target.value)}>
                  {LAUNCH.map((l) => (
                    <option key={l.code} value={l.code}>
                      {l.label /* Al-Noor 4 Nov 2026: his names, locales.py AL_NOOR */}
                    </option>
                  ))}
                </select>
                <div className="help">
                  Eleat&rsquo;s own words exist in four languages so far. What
                  you speak is the question below — answer it and it is how the
                  next one gets chosen.
                </div>
              </Fld>

              {/* ⚠️ THE OPPOSITE QUESTION TO THE ONE ABOVE. Al, 9 September
                  2026: *"id still keep the language ask in the profile. with
                  that we can guage how many eleaters speak differnt
                  languages."*  Above: what should the APP be in — one answer.
                  Here: what do you SPEAK — many, including languages Eleat
                  cannot show a single button in.
                  ⚠️ THE "Other" BOX IS THE MOST VALUABLE THING HERE and the
                  easiest to throw away: it is the only place Hakka, Teochew,
                  Nepali or Punjabi can appear. Free text on purpose. */}
              <Fld label="Other languages you speak">
                {/* Al-Noor, 4 Nov 2026: one language per row, his 23 in his order —
                    the main four (the app's own, LAUNCH), then the additional ones. */}
                <div className="picklist">
                  <div className="langhd">Main languages</div>
                  {MAJOR.filter((l) => LAUNCH.some((x) => x.code === l.code)).map((l) => {
                    const on = draft.languages_spoken.includes(l.code);
                    return (
                      <button key={l.code} type="button" disabled={!open}
                        className={on ? 'pickrow on' : 'pickrow'} aria-pressed={on}
                        onClick={() => set('languages_spoken',
                          on ? draft.languages_spoken.filter((c) => c !== l.code)
                            : [...draft.languages_spoken, l.code])}>
                        <span>{l.label}</span>
                        <span className="tick">{on ? '✓' : ''}</span>
                      </button>
                    );
                  })}
                  <div className="langhd">Additional languages</div>
                  {MAJOR.filter((l) => !LAUNCH.some((x) => x.code === l.code)).map((l) => {
                    const on = draft.languages_spoken.includes(l.code);
                    return (
                      <button key={l.code} type="button" disabled={!open}
                        className={on ? 'pickrow on' : 'pickrow'} aria-pressed={on}
                        onClick={() => set('languages_spoken',
                          on ? draft.languages_spoken.filter((c) => c !== l.code)
                            : [...draft.languages_spoken, l.code])}>
                        <span>{l.label}</span>
                        <span className="tick">{on ? '✓' : ''}</span>
                      </button>
                    );
                  })}
                </div>
                <input className="in" placeholder="Type your language"
                  style={{ marginTop: 10 }} readOnly={!open}
                  value={draft.languages_spoken_other}
                  onChange={(e) => set('languages_spoken_other', e.target.value)} />
              </Fld>
            </div>

            {/* ── PREFERENCES ─────────────────────────────────────────────
                ⚠️ FIVE ROWS, NO SUBTITLES. `08e_Settings.html` lines 527–538:
                Notifications · Location · Privacy & data · Terms & Privacy
                Policy · Log out. The three subtitles React had were invented.
                ⚠️ AND "Notification settings" IS NOT ON THIS LIST — the locked
                Notifications row is the only one, and 08i is reached from
                there. A sixth row would be a control Al never approved. */}
            <div className="sec">PREFERENCES</div>
            <nav className="plist">
              {/* the locked row opens 08i_Notification_Settings.html — the
                  preferences, not the inbox (audit, 7 Oct 2026) */}
              <Row href="?notifsettings=1" t="Notifications" />
              <Row href="?location=1" t="Location" />
              {/* ⚠️ THESE TWO POINT AT HELP IN THE LOCKED FILE TOO, marked
                  `data-not-built="1"`. They are not dead controls — they are
                  honest ones: the page does not exist yet, so the row goes
                  somewhere real rather than nowhere. */}
              {/* the locked rows pointed at Help only because the legal pages
                  were not built (data-not-built); they are now, on eleat.app
                  (audit, 7 Oct 2026) */}
              {/* inside the app, never out to the website (Al-Noor, 14 Oct 2026) */}
              {/* ⚠️ BACK FROM A POLICY LANDS HERE, NOT AT THE TOP — Al-Noor,
                  21 Oct 2026. Where they were is kept for the trip there. */}
              <Row href="?legal=privacy" t="Privacy & data" onClick={rememberSettingsPlace} />
              <Row href="?legal=terms" t="Terms & Privacy Policy" onClick={rememberSettingsPlace} />
              <Row href="?legal=cookies" t="Cookies" onClick={rememberSettingsPlace} />
              <LogOut />
            </nav>

            {saveErr && <p className="err">{saveErr}</p>}

            {/* ⚠️ AT THE BOTTOM, BELOW PREFERENCES — Al, audit item 10.6,
                answered 9 Sep 2026: *"at the bottom is the correct one."*  It
                used to sit between ACCOUNT and PREFERENCES, which reads as a
                boundary: everything above is covered, everything below is not.
                One handler reads the whole screen, so that was never true. */}
            <button type="button" className="save" disabled={saving} onClick={save} data-i18n-ctx="form">
              {saving ? 'Saving…' : saved ? 'Saved' : 'Save changes'}
            </button>

            <button type="button" className="danger"
              onClick={() => setConfirmDelete(true)}>Delete account</button>

            {confirmDelete && <DeleteSheet onClose={() => setConfirmDelete(false)} />}
          </>
        )}
      </main>

      <TabBar active="profile" />
    </div>
  );
}

/* ⚠️ ONE FIELD, DRAWN ONE WAY. `08e_Settings.html .fld` → `.lab` → the
 * control. `optional`, `required` and `badge` are the three things its label
 * can carry, and each is in the locked markup: `.opt`, `.req`, `.verified`. */
function Fld({ label, aside, optional, required, badge, children }) {
  return (
    <div className="fld">
      <div className="lab">
        {label}
        {required && <span className="req"> *</span>}
        {optional && <span className="opt">Optional</span>}
        {aside && <span className="aside"> {aside}</span>}
        {badge && <span className="verified">{badge}</span>}
      </div>
      {children}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️ THE VERIFY BUTTON — DRAWN ON THE LOCKED SCREEN, ANSWERED BY NOTHING
 *
 * `08e_Settings.html` has had `<button class="vbtn" id="stVerify">Verify` and
 * a six-box code panel since it was locked, and there was NO ROUTE in this
 * backend that could verify an email on an account that already exists.
 * Worse: `updateMine` sets `email_verified=false` whenever the address
 * changes, so changing your email marked it unverified **for ever**.
 * `POST /settings/me/email/verify/start` and `/finish` were added the same
 * hour as this component, reusing the OTP machinery joining already uses.
 *
 * ⚠️ SIX BOXES, ONE VALUE. They look like six inputs and are one string —
 * typing moves forward, Backspace on an empty box moves back, and a pasted
 * code fills all six. Six pieces of state would be six ways to get out of
 * step with what is actually sent.
 * ══════════════════════════════════════════════════════════════════════════ */
function VerifyEmail({ me, setMe }) {
  const [panel, setPanel] = useState(false);
  const [code, setCode] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  async function start() {
    if (busy) return;
    setBusy(true); setMsg('');
    try {
      const d = await call('/settings/me/email/verify/start', { method: 'POST', auth: true });
      if (d.already_verified) { setMsg('That address is already verified.'); }
      else { setPanel(true); setMsg(''); }
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  }

  async function confirm() {
    if (busy) return;
    /* never greyed out (Al-Noor, 10 Oct 2026): pressed too early it says what is missing */
    if (code.length < 6) { setMsg('Enter the code we sent you.'); return; }
    setBusy(true); setMsg('');
    try {
      const d = await call('/settings/me/email/verify/finish',
        { method: 'POST', auth: true, body: { code } });
      setMe((prev) => ({ ...prev, ...d }));
      setPanel(false);
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  }

  return (
    <>
      <button type="button" className="vbtn" onClick={start}>
        {busy ? '…' : 'Verify'}
      </button>
      {panel && (
        <div className="otp">
          <div className="lab" style={{ marginBottom: 0 }}>
            Enter the code we sent to <b>{me.email}</b>
          </div>
          <CodeBoxes value={code} onChange={setCode} />
          <div className="otprow">
            <button type="button" className="otpgo"
              onClick={confirm}>Confirm</button>
            <button type="button" className="otpalt"
              onClick={start}>Send it again</button>
            <button type="button" className="otpalt"
              onClick={() => { setPanel(false); setCode(''); setMsg(''); }}>Cancel</button>
          </div>
          {msg && <div className="help">{msg}</div>}
        </div>
      )}
      {!panel && msg && <div className="help">{msg}</div>}
    </>
  );
}

function CodeBoxes({ value, onChange }) {
  const digits = value.padEnd(6, ' ').slice(0, 6).split('');
  function typed(i, s) {
    /* a pasted code fills every box from wherever it was dropped */
    const only = s.replace(/\D/g, '');
    if (!only) { onChange((value.slice(0, i) + value.slice(i + 1)).slice(0, 6)); return; }
    const next = (value.slice(0, i) + only + value.slice(i + only.length)).slice(0, 6);
    onChange(next);
    const box = document.getElementById(`otp${Math.min(5, i + only.length)}`);
    if (box) box.focus();
  }
  return (
    <div className="otpboxes">
      {digits.map((d, i) => (
        <input key={i} id={`otp${i}`} className="otpbox" inputMode="numeric"
          maxLength={6} value={d.trim()} aria-label={`Digit ${i + 1}`}
          onChange={(e) => typed(i, e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && !d.trim() && i > 0) {
              const prev = document.getElementById(`otp${i - 1}`);
              if (prev) prev.focus();
            }
          }} />
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️ ONE CONTROL, TWO LABELS, AND THE STATE DECIDES — never the screen.
 *
 * Al, 16 September 2026: *"You sign up, and you have to sign up with a
 * confirmation… phone number, password, confirmation of password, sign in
 * OTP."*  That covers everybody who joins from today and NOBODY who joined
 * before it. Migration 082 gave an Eleater a password to sign in with; an
 * account made by code could only ever sign in by code, for ever, with no
 * route out of it. This is the control that changes that.
 *
 * ⚠️ `has_password` NOW COMES BACK FROM THE ROUTE — `user_has_a_password()`,
 * migration 082, added to the select the same hour. Two separate rows would
 * be two controls for one fact, and the wrong one would be showing the day
 * the fact changed.
 *
 * ⚠️ CHANGING ASKS FOR THE CURRENT ONE. Setting the first cannot — there is
 * nothing to ask for — so the box is put away rather than left empty, because
 * an empty required box is a form that looks broken.
 * ══════════════════════════════════════════════════════════════════════════ */
function SetPassword({ me, setMe, open }) {
  const [panel, setPanel] = useState(false);
  const [cur, setCur] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  /* ⚠️ THREE BOXES, THREE EYES, AND EACH REMEMBERS ITS OWN STATE.
     `08e_Settings.html` puts `stPwCurrentEye`, `stPwNewEye` and `stPwNew2Eye`
     on the three password boxes — somebody typing a password they cannot see
     into a box they cannot check is how "those two do not match" happens.
     ⚠️ THE ICON IS THE STATE, NOT THE WISH — Al, 17 September 2026. `Eye.jsx`
     already holds that rule for Join, Sign in and Reset; a fourth copy of the
     SVG here would be a fourth place for the crossed eye to stop matching. */
  const [see, setSee] = useState({ cur: false, pw: false, pw2: false });
  const eye = (k) => ({ on: see[k], onToggle: () => setSee((v) => ({ ...v, [k]: !v[k] })) });
  const has = !!me.has_password;

  async function go() {
    if (busy) return;
    const n = needs(pw);
    if (n) { setMsg(n); return; }
    if (pw !== pw2) { setMsg('Those two do not match.'); return; }
    setBusy(true); setMsg('');
    try {
      /* ⚠️ confirm_password IS REQUIRED by the route (it re-checks the two
         match) — without it every change answered 422 "Validation failed";
         and the new token it hands back is kept, or this device is signed out
         by its own success (audit, 7 Oct 2026) */
      const r = await call('/auth/password/set', {
        method: 'POST', auth: true,
        body: has ? { current_password: cur, new_password: pw, confirm_password: pw2 }
                  : { new_password: pw, confirm_password: pw2 },
      });
      if (r?.token) replaceToken(r.token);
      setMe((prev) => ({ ...prev, has_password: true }));
      setPanel(false); setCur(''); setPw(''); setPw2('');
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  }

  return (
    <div className="fld">
      <div className="lab">Password</div>
      <div className="two">
        <div className="in locked" style={{ flex: 2 }}>{has ? '••••••••' : '—'}</div>
        {/* ⚠️ `disabled` BECAUSE A <button> HAS NO readonly — the exact trap
            the dial code and the gender dropdown fell into on the locked
            screen, recorded in its own comment. */}
        <button type="button" className="vbtn" disabled={!open}
          onClick={() => setPanel((p) => !p)}>
          {has ? 'Change password' : 'Set a password'}
        </button>
      </div>
      <div className="help">A password lets you sign in without waiting for a code.</div>

      {panel && (
        <div className="otp">
          {has && (
          <Fld label="Current password">
            <div className="two">
              <input className="in" type={see.cur ? 'text' : 'password'}
                autoComplete="current-password" placeholder="Your current password" value={cur}
                onChange={(e) => setCur(e.target.value)} />
              <Eye {...eye('cur')} />
            </div>
          </Fld>
          )}
          <Fld label="New password">
            <div className="two">
              <input className="in" type={see.pw ? 'text' : 'password'}
                autoComplete="new-password" placeholder="Choose a password" value={pw}
                onChange={(e) => setPw(e.target.value)} />
              <Eye {...eye('pw')} />
            </div>
            {/* the whole rule, each line ticked as it is met — Al-Noor, 9/10 Oct 2026 */}
            <PwChecklist pw={pw} />
          </Fld>
          <Fld label="Type it again">
            <div className="two">
              <input className="in" type={see.pw2 ? 'text' : 'password'}
                autoComplete="new-password" placeholder="Type it again" value={pw2}
                onChange={(e) => setPw2(e.target.value)} />
              <Eye {...eye('pw2')} />
            </div>
          </Fld>
          {msg && <div className="help">{msg}</div>}
          <div className="otprow">
            <button type="button" className="otpgo"
              onClick={go}>Save password</button>
            <button type="button" className="otpalt"
              onClick={() => { setPanel(false); setMsg(''); }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️ DELETING IS PERMANENT AND THE SHEET SAYS SO — Al, 9 September 2026:
 * *"if any one wants to delete they should be allowed to delete. all ask is
 * if they want deletion and its permanent and also cannot be restored."*
 *
 * Every word below is quoted from `08e_Settings.html`, including the two
 * lists. ⚠️ THE "What stays" LIST IS THE PART THAT MATTERS: a screen implying
 * everything vanishes when some of it does not is what turns a lawful
 * deletion into a broken promise. A published review stays, with the name
 * removed, because a restaurant's rating cannot be rewritten after the fact —
 * and `08h_Help.html` has always said so.
 *
 * ⚠️ AND THE BUTTON IS LABELLED WITH THE ACTION. "Delete my account", never
 * "Continue" — somebody skimming reads the button, and "Continue" tells them
 * nothing about what is about to happen.
 * ══════════════════════════════════════════════════════════════════════════ */
function DeleteSheet({ onClose }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  async function go() {
    setBusy(true); setMsg('');
    try {
      await call('/settings/me', { method: 'DELETE', auth: true });
      clearToken();
      window.location.replace('?');
    } catch (e) { setMsg(e.message); setBusy(false); }
  }

  return (
    <div className="sheet-wrap">
      <div className="sheet-scrim" onClick={onClose} />
      <div className="sheet" role="dialog" aria-modal="true" aria-label="Delete your account?">
        <div className="sheet-grab" />
        <h2 className="sheet-h">Delete your account?</h2>
        <p className="sheet-p">
          This is permanent. Your account cannot be restored and nobody at
          Eleat can bring it back.
        </p>

        <div className="sheet-lbl">What is deleted</div>
        <ul className="sheet-list">
          <li>Your name, date of birth and how we reach you</li>
          <li>Saved places, reservations and notifications</li>
          <li>Your Eleater ID</li>
        </ul>

        <div className="sheet-lbl">What stays</div>
        <ul className="sheet-list">
          <li>
            Reviews you have published, with your name removed — a
            restaurant&rsquo;s rating cannot be rewritten after the fact
          </li>
        </ul>

        {msg && <p className="err">{msg}</p>}
        <button type="button" className="sheet-go" disabled={busy} onClick={go}>
          {busy ? 'Deleting…' : 'Delete my account'}
        </button>
        <button type="button" className="sheet-no" onClick={onClose}>
          Keep my account
        </button>
      </div>
    </div>
  );
}

/* ⚠️ A ROW, NOT A BUTTON THAT LOOKS LIKE ONE. `08e_Settings.html` line 538
 * draws Log out as the fifth `.row` in PREFERENCES, chevron and all, so it
 * sits in the list rather than beside it. */
function LogOut() {
  return (
    /* ⚠️ THE SAME MARKUP AS `Row`, so it sits IN the list rather than beside
       it. `.plist .item` is what draws a row in this app; a `<button
       class="row">` would have carried none of it and Log out would have
       looked like a control that fell out of the group. */
    <SignOutButton className="item">
      <span className="lab"><span className="t">Sign out</span></span>
      <span className="chev" aria-hidden="true">›</span>
    </SignOutButton>
  );
}

/* ⚠️ SIGN OUT ASKS FIRST — Al-Noor, 21 Oct 2026: "it didn't even ask… should
   have said, do you want to sign out? Yes or no". The gate sheet's own shape
   (`.egate`), so it looks like every other question Eleat asks. */
function SignOutButton({ className, children }) {
  const [ask, setAsk] = useState(false);
  useEffect(() => {
    if (!ask) return undefined;
    const esc = (e) => { if (e.key === 'Escape') setAsk(false); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [ask]);
  return (
    <>
      <button type="button" className={className} onClick={() => setAsk(true)}>{children}</button>
      {ask ? (
        <div className="egate" role="dialog" aria-modal="true" aria-labelledby="signoutH">
          <div className="egate-veil" onClick={() => setAsk(false)} />
          <div className="egate-card">
            <div className="egate-grip" />
            <h3 className="egate-h" id="signoutH">Sign out of Eleat?</h3>
            <button className="egate-go" type="button"
              onClick={() => { clearToken(); window.location.replace('?'); }}>
              Sign out
            </button>
            <button className="egate-no" type="button" onClick={() => setAsk(false)}>Cancel</button>
          </div>
        </div>
      ) : null}
    </>
  );
}

/* ── the phone number, split and rejoined ──────────────────────────────────
 * ⚠️ STORED AS ONE STRING, SHOWN AS TWO BOXES. `users.phone` is one column and
 * always has been; the two boxes are Al's instruction about the SCREEN, not a
 * change to the schema. So the split happens here and nowhere else.
 *
 * ⚠️ THE LONGEST DIAL CODE WINS. +1 and +1868 (Trinidad) both start the same
 * way, and +6 5 and +65 would both match a Singapore number — matching the
 * first code found would put a Trinidad number under the United States. */
/* splitPhone / joinPhone live in phone.jsx now — joining by number uses them too. */

/* ⚠️ ORDER IS NOT A DIFFERENCE. Tapping two chips off and back on in the other
 * order is the same set of languages, and sending it as a change would write
 * a row nobody altered. */
function sameList(a, b) {
  const x = [...(a || [])].sort();
  const y = [...(b || [])].sort();
  return x.length === y.length && x.every((v, i) => v === y[i]);
}

/* ── NOTIFICATIONS ────────────────────────────────────────────────────────
 * GET /notifications/me · POST /notifications/:id/read · POST /read-all
 */
function Notifications() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let live = true;
    call('/notifications/me', { auth: true })
      .then((d) => live && setRows(Array.isArray(d) ? d : (d.results || d.notifications || [])))
      .catch((e) => live && setErr(e.message));
    return () => { live = false; };
  }, []);

  /* ⚠️ MARKED READ ON THE SERVER FIRST, THEN ON SCREEN. Showing it read and
     letting the call fail quietly means it comes back unread on the next
     device — and the Eleater has no idea why. */
  async function markRead(id) {
    try {
      await call(`/notifications/${id}/read`, { method: 'POST', auth: true });
      setRows((r) => r.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n)));
    } catch (e) { setErr(e.message); }
  }

  async function markAll() {
    try {
      await call('/notifications/read-all', { method: 'POST', auth: true });
      const now = new Date().toISOString();
      setRows((r) => r.map((n) => (n.read_at ? n : { ...n, read_at: now })));
    } catch (e) { setErr(e.message); }
  }

  const unread = (rows || []).filter((n) => !n.read_at).length;

  return (
    <div className="home">
      <div className="top">
        {/* ⚠️ BACK GOES HOME — `08g_Notifications.html` links its back control
            to `10_Home_LOCKED.html`. Al reached this screen by tapping the
            BELL ON THE HOMEPAGE, and landed on Profile when he came back:
            *"bell on homepage goes to notifications and then the back button
            goes to profile."* The bell is on Home, so back is Home. */}
        <a className="back" href="?" aria-label="Home">‹</a>
        <h1 className="heroline">Notifications</h1>
        {/* ══════════════════════════════════════════════════════════════
            ⚠️ THE GEAR — `08g_Notifications.html .nprefs`, and this is the
            ONLY way into `08i_Notification_Settings.html` anywhere in Eleat.
            Its PREFERENCES list on Settings is five rows and "Notification
            settings" is not one of them; the door is here, on the screen the
            notifications actually arrive on.

            ⚠️ REACT HAD IT ON SETTINGS INSTEAD, as a sixth row Al never
            approved — and moving it to where the locked file puts it would
            have left 08i unreachable if this had not gone in at the same
            time. That is the dead-screen fault in reverse: not a control
            that does nothing, a screen nothing opens. */}
        <a className="nprefs" href="?notifsettings=1"
          title="How we reach you" aria-label="How we reach you">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round"
            strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09A1.65 1.65 0 008 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06A1.65 1.65 0 004.6 15a1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06A1.65 1.65 0 009 4.6a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06A1.65 1.65 0 0019.4 9c.14.63.67 1.09 1.31 1.1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z" />
          </svg>
        </a>
      </div>

      <main>
        {err && <p className="err">{err}</p>}
        {!rows && !err && <p className="mut">Loading…</p>}

        {rows && unread > 0 && (
          <button type="button" className="pbtn" onClick={markAll}>
            Mark all read ({unread})
          </button>
        )}

        {rows && rows.length === 0 && (
              <>
                {/* ⚠️ `08g_Notifications.html` — "You're all caught up" over a
                    line saying what will land here. "Nothing yet." says the
                    screen is empty; it does not say the screen is WORKING. */}
                <p className="mut">You’re all caught up</p>
                <p className="mut">
                  Deals, reservation updates and Zaryna’s picks will appear here.
                </p>
              </>
            )}

        {rows && rows.map((n) => (
          <button key={n.id} type="button"
            className={n.read_at ? 'nrow' : 'nrow unread'}
            onClick={() => {
              if (!n.read_at) markRead(n.id);
              /* a notice opens what it is about, as 08g_Notifications.html's
                 rows do (audit, 7 Oct 2026: it only marked itself read) */
              const d = n.data || {};
              const to = d.dined ? `?dined=${encodeURIComponent(d.dined)}`   // "Did you make it to …?" (227)
                : d.review_id ? '?reviews=1'
                : d.reservation_id ? '?reservations=1'
                : d.restaurant_id ? `?venue=${encodeURIComponent(d.restaurant_id)}` : null;
              if (to) go(to);
            }}>
            <span className="ntitle">{n.title || n.type}</span>
            {n.body && <span className="nbody">{n.body}</span>}
          </button>
        ))}
      </main>

      <TabBar active="profile" />
    </div>
  );
}
